'use client';

import { useCallback, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { ListingCard } from '@/src/components/ListingCard';
import type { ListingCardListing } from '@/src/components/ListingCard/types';
import { CAROUSEL_CARD_IMAGE_SIZES } from '@/src/components/ListingCarousel';
import { LucideIcon } from '@/src/components/LucideIcon';
import { Link, useRouter } from '@/src/i18n/navigation';
import { usePrepareMapForListingNavigation } from '@/src/lib/hooks/map';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import { seeAllLink } from './featuredRentals.styles';

type FeaturedRentalListingCardProps = {
  listing: ListingCardListing;
  priority: boolean;
};

export function FeaturedRentalListingCard({ listing, priority }: FeaturedRentalListingCardProps) {
  const isDesktop = useIsDesktop();
  const prepareMapForListingNavigation = usePrepareMapForListingNavigation();
  const handleNavigate = useCallback(
    (nextListing: ListingCardListing) => {
      prepareMapForListingNavigation(nextListing.maloneLotIdEncoded);
    },
    [prepareMapForListingNavigation],
  );

  return (
    <ListingCard
      listing={listing}
      sizes={CAROUSEL_CARD_IMAGE_SIZES}
      prefetch={false}
      priority={priority && isDesktop}
      onNavigate={handleNavigate}
    />
  );
}

type FeaturedRentalsSeeAllLinkProps = {
  href: string;
  label: string;
};

export function FeaturedRentalsSeeAllLink({ href, label }: FeaturedRentalsSeeAllLinkProps) {
  const router = useRouter();
  const prefetchedHrefs = useRef(new Set<string>());
  const prefetchRoute = useCallback(() => {
    if (prefetchedHrefs.current.has(href)) return;

    prefetchedHrefs.current.add(href);
    router.prefetch(href as Parameters<typeof router.prefetch>[0]);
  }, [href, router]);

  return (
    <Link
      href={href}
      prefetch={false}
      className={seeAllLink}
      onFocus={prefetchRoute}
      onPointerEnter={prefetchRoute}
      onPointerDown={prefetchRoute}
    >
      {label}
      <LucideIcon icon={ArrowRight} size="sm" />
    </Link>
  );
}
